import React, { FC, useState } from "react";
import {
    Card,
    CardContent,
    Grid,
    Typography,
    IconButton,
    CardActions,
    Stack,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    Box,
    alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { MoreVert } from "@mui/icons-material";
import { useDeletePaymentMethod } from "@features/payments/hooks/useDeletePaymentMethod";
import { useSetDefaultPaymentMethod } from "@features/payments/hooks/useSetDefaultPaymentMethod";
import { medium } from "@styles/fontWeights";
import { primaryBlue } from "@styles/colors";

type PaymentCard = {
    card: any;
    id: string;
    brand: string;
    last4: string;
    customer: string;
    exp_month: number;
    exp_year: number;
};

type SinglePaymentCardProps = {
    card: PaymentCard;
    defaultSource: string;
};

const SinglePaymentCard: FC<SinglePaymentCardProps> = ({ card, defaultSource }) => {
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [defaultCard, setDefaultCard] = useState<string | null>(null);
    const [errorText, setErrorText] = useState<string>('');
    const { deletePaymentMethod, isDeletePaymentMethod } = useDeletePaymentMethod({ setErrorText })
    const { setDefaultPaymentMethod, iseSettingDefaultPaymentMethod } = useSetDefaultPaymentMethod({ setErrorText })

    const handleSetDefault = (cardId: string) => {
        setDefaultCard(cardId);
        setDefaultPaymentMethod({ payment_id: cardId, customer_id: card.customer })
    };

    const getCardLogo = (brand: string): string => {
        switch (brand.toLowerCase()) {
            case "visa":
                return "https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png";
            case "mastercard":
                return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQl9iuVaaKs7wpjtwKnozaP3jl9rMqxK_j-Ew&s";
            case "amex":
                return "https://cdn4.iconfinder.com/data/icons/logos-and-brands/512/16_Amex_Credit_Card_logo_logos-512.png";
            case "discover":
                return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRvCTp-Y7Msyl9pmjaus8Hlcu99I9apoGx9aw&s";
            case "jcb":
                return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR-TJ_yiPGULbS6OV-BcN3ZyVBcFscCPoMkYA&s";
            case "unionpay":
                return "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/UnionPay_logo.svg/1280px-UnionPay_logo.svg.png";
            default:
                return "https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg";
        }
    };

    return (
        <Grid item xs={12} key={card.id}>
            <Card variant="outlined">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <CardContent style={{ display: "flex", alignItems: "center" }}>
                        <img
                            src={getCardLogo(card.card.brand)}
                            alt={`${card.brand} Logo`}
                            style={{ width: "40px", marginRight: "15px" }}
                        />
                        <div>
                            <Stack direction='row' spacing={2}>
                                <Typography variant="body1">
                                    {card.card.brand} •••• {card.card.last4}
                                </Typography>
                                {defaultSource === card.id ? (
                                    <>  <Box px={2} py={1} bgcolor={alpha(primaryBlue, 0.2)} borderRadius='5px'>
                                        <Typography color={primaryBlue} textTransform="capitalize" fontWeight={medium} style={{ fontSize: '13px', lineHeight: '13px' }}>
                                            Default
                                        </Typography>
                                    </Box></>
                                ) : null}
                            </Stack>
                            <Typography variant="body2" color="textSecondary">
                                Expires {card.card.exp_month}/{card.card.exp_year}
                            </Typography>
                        </div>
                    </CardContent>
                    <CardActions>
                        {/* <IconButton
                            aria-label="delete"
                            onClick={() => deletePaymentMethod({ payment_id: card?.id })}
                            disabled={isDeletePaymentMethod}
                        >
                            {isDeletePaymentMethod ?
                                <CircularProgress /> :
                                <DeleteIcon />
                            }
                        </IconButton> */}
                        <FormControl size="small">
                            <Select
                                value={defaultCard === card.id ? "default" : ""}
                                displayEmpty
                                onChange={(event) => {
                                    handleSetDefault(card.id);
                                }}
                                IconComponent={() => null}
                                sx={{
                                    textAlign: 'right',
                                    alignSelf: 'end',
                                    minWidth: 0,
                                    '& .MuiSelect-select': {
                                        padding: '0 !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                                renderValue={() => (
                                    <MoreVert
                                        sx={{
                                            padding: '0 !important',
                                            color: 'gray',
                                        }}
                                    />
                                )}
                            >
                                <MenuItem>Set as Default</MenuItem>
                            </Select>
                        </FormControl>
                    </CardActions>
                </Stack>
            </Card>
        </Grid>
    );
};

export default SinglePaymentCard;
